import { type PropsWithChildren, useEffect, useState, useMemo } from "react";
import {
  useLogto,
  LogtoClient,
  LogtoContext,
  Prompt,
  LogtoNativeConfig,
} from "@logto/rn";
import { UserScope, type SignInOptions } from "@logto/client";
import { Platform } from "react-native";
import { EventHandler, MessageBus } from "./message.bus";
import AsyncStorage from "@react-native-async-storage/async-storage";

export type SessionMessageBusEvent = "SIGN_IN_REQUIRED";

// This hook can be used to access the user info.
export function useSession(): Omit<
  ReturnType<typeof useLogto>,
  "signIn" | "signOut"
> & {
  signIn: (options: Omit<SignInOptions, "redirectUri">) => Promise<void>;
  signOut: (redirectUri?: string) => Promise<void>;
  mainResource: string;
} {
  const value = useLogto();

  return {
    ...value,
    mainResource: LOGTO_CLIENT.logtoConfig.resources?.[0]!,

    signIn: (options) => {
      const redirectUri =
        Platform.OS === "web"
          ? process.env.EXPO_PUBLIC_AUTH_WEB_REDIRECT_URI
          : process.env.EXPO_PUBLIC_AUTH_MOBILE_REDIRECT_URI;

      if (!redirectUri) {
        throw new TypeError("Redirect URI is not defined");
      }

      return value.signIn({
        ...options,
        redirectUri,
      });
    },

    signOut: async (redirectUri) => {
      await AsyncStorage.multiRemove(["impersonation", "impersonation-exp"]);

      return value.signOut(redirectUri);
    },
  };
}

export function useAccessTokenClaims() {
  const {
    getAccessTokenClaims,
    getIdTokenClaims,
    isAuthenticated,
    mainResource,
  } = useSession();

  const [claims, setClaims] = useState<
    Awaited<ReturnType<typeof getAccessTokenClaims>> & {
      externalClaims: {
        userId: string;
        email: string;
        whitelisted?: boolean;
      };
      roles: string[];
    }
  >();

  useEffect(() => {
    if (isAuthenticated) {
      Promise.all([getAccessTokenClaims(mainResource), getIdTokenClaims()])
        .then(([ar, ir]) =>
          setClaims({
            ...(ar as any),
            roles: ar.roles ?? ir.roles,
          })
        )
        .catch((_) => {});
    }
  }, [isAuthenticated, getAccessTokenClaims, mainResource]);

  return claims;
}

export const LOGTO_MESSAGE_BUS = new MessageBus<SessionMessageBusEvent>();

export const LOGTO_CLIENT = new LogtoClient({
  endpoint: process.env.EXPO_PUBLIC_AUTH_ENDPOINT!,
  appId: process.env.EXPO_PUBLIC_AUTH_APP_ID!,
  resources: [process.env.EXPO_PUBLIC_AUTH_RESOURCE!],
  prompt: [Prompt.Login, Prompt.Consent],
  scopes: [UserScope.Roles, "create:organization"],
});

export function SessionProvider({ children }: PropsWithChildren) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    (async () => {
      const isAuthenticated = await LOGTO_CLIENT.isAuthenticated();
      setIsAuthenticated(isAuthenticated);

      // Mark the client as initialized.
      setIsInitialized(true);
    })();
  }, [LOGTO_CLIENT]);

  const memorizedContextValue = useMemo(
    () => ({
      client: LOGTO_CLIENT,
      isAuthenticated,
      isInitialized,
      setIsAuthenticated,
    }),
    [LOGTO_CLIENT, isAuthenticated, isInitialized]
  );

  return (
    <LogtoContext.Provider value={memorizedContextValue}>
      <SessionListener>{children}</SessionListener>
    </LogtoContext.Provider>
  );
}

function SessionListener(props: PropsWithChildren) {
  const { signOut } = useSession();

  useEffect(() => {
    const handler: EventHandler<SessionMessageBusEvent> = (event) => {
      if (event === "SIGN_IN_REQUIRED") {
        signOut();
      }
    };

    LOGTO_MESSAGE_BUS.subscribe("SIGN_IN_REQUIRED", handler);

    return () => {
      LOGTO_MESSAGE_BUS.unsubscribe("SIGN_IN_REQUIRED", handler);
    };
  }, []);

  return props.children;
}
